import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { definePersistenceEntry, type ModulePersistenceEntry, type SliceDeclaration } from '@krp/client-runtime'

/**
 * Тема оформления (UI-STYLE): светлая или тёмная, переключатель один. Пока
 * человек сам не выбирал (`choice === null`, в хранилище устройства пусто),
 * тема определяется временем суток; его выбор — намерение за этим устройством:
 * срок `device`, запись общая для всех, кто садится за устройство, и после
 * перезагрузки он главнее времени суток. Тема всегда кладётся `data-theme`
 * на `<html>`.
 */
export type ThemeName = 'light' | 'dark'

export const THEME_NAMES: readonly ThemeName[] = ['light', 'dark']

/** Выбор человека; `null` — ещё не выбирал, решает время суток. */
export type ThemeChoice = ThemeName | null

/** День — с 6:00 до 20:00 по местному времени устройства; остальное — ночь. */
export const DAY_STARTS_AT_HOUR = 6
export const NIGHT_STARTS_AT_HOUR = 20

export interface ThemeState {
  choice: ThemeChoice
}

const initialState: ThemeState = { choice: null }

/** Тема по времени суток. */
export function themeForTime(time: Date): ThemeName {
  const hour = time.getHours()
  return hour >= DAY_STARTS_AT_HOUR && hour < NIGHT_STARTS_AT_HOUR ? 'light' : 'dark'
}

/** Тема, которая действует: выбор человека, а без него — по времени суток. */
export function resolveTheme(choice: ThemeChoice, time: Date = new Date()): ThemeName {
  return choice ?? themeForTime(time)
}

const themeSlice = createSlice({
  name: 'theme',
  initialState,
  reducers: {
    themeChosen(state, action: PayloadAction<ThemeChoice>) {
      state.choice = action.payload
    },
  },
})

export const { themeChosen } = themeSlice.actions
export const themeReducer = themeSlice.reducer

export const selectThemeChoice = (state: ThemeState): ThemeChoice => state.choice

/** Выбор темы от корня состояния: форму `RootState` слайс не импортирует, ему достаточно своего ключа. */
export const selectTheme = (state: { theme: ThemeState }): ThemeChoice => selectThemeChoice(state.theme)

/** Срок жизни слайса темы (D-070). Composition добавляет префикс оболочки (`shell.theme`). */
export const THEME_SLICES: readonly SliceDeclaration[] = [
  { name: 'theme', lifetime: 'device', reason: 'тему выбирает человек за этим устройством; она переживает перезагрузку' },
]

/** Проверка прочитанного (D-072): чужое значение (и прежнее «как в системе») отбрасывается, тема снова по времени суток. */
export function decodeThemeChoice(raw: unknown): ThemeName | undefined {
  return THEME_NAMES.find((name) => name === raw)
}

export const THEME_PERSISTENCE: readonly ModulePersistenceEntry<ThemeState>[] = [
  definePersistenceEntry({
    key: 'shell.theme',
    slice: 'theme',
    lifetime: 'device',
    scope: 'device',
    version: 1,
    pick: selectThemeChoice,
    decode: decodeThemeChoice,
    restore: (value) => themeChosen(value),
  }),
]

/** Тема → атрибут на `<html>`: токены объявлены в `:root` и обязаны меняться выше любого нашего узла. */
export function applyTheme(root: HTMLElement, theme: ThemeName): void {
  root.setAttribute('data-theme', theme)
}
